// ─── Section CRÉDIT d'une ligne prévue (17/08) ────────────────────
// Rendue par `FeuilleLigne` quand la bascule « Crédit » est active. Les
// champs suivent `services/credit.js` (`normaliserCredit`) au plus près —
// les valeurs restent des CHAÎNES ici, la normalisation/le parsing se font
// à l'enregistrement (`services/lignes.js`), comme le reste de la feuille.
import React, { useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { ChoixPuces, SectionPliable, Stepper, stylesForm, useUI, useT } from '@mop/core';
import { calculerCredit } from './services/credit';
import { formatEuro } from './services/calculs';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const libelleMois = (mk, t) => {
  const [y, m] = String(mk || '').split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk || ''} ${y || ''}`.trim();
};

export default function SectionCredit({
  valeur = {}, onChange, onDemanderMois,
  debutLigne, montantLigne, jourLigne,
  avanceOuvert, onToggleAvance,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const v = valeur;
  const set = (k, val) => onChange?.({ [k]: val });
  const params = v.mode === 'params';
  const nb = (x) => parseFloat(String(x).replace(',', '.')) || 0;
  const entier = (x) => parseInt(x, 10) || 0;

  // Durée : `moisExact` FAIT FOI (ce que lit le moteur), `unite` ne choisit
  // que l'AFFICHAGE (Cédric, 17/08). À la différence de `SelecteurDuree`
  // (qui réinterprète volontairement le nombre sans le convertir — décidé
  // pour les horizons/filtres), la durée d'un crédit est UNE quantité
  // réelle : 24 mois DOIT rester 2 ans, pas devenir « 24 ans » en changeant
  // d'unité. D'où ce contrôle dédié plutôt que le composant générique.
  const duree = v.dureeUnite || { moisExact: 0, unite: 'mois' };
  const dureeMois = duree.moisExact || 0;
  const dureeAffichee = duree.unite === 'annee'
    ? Math.round((dureeMois / 12) * 100) / 100
    : dureeMois;
  const dureeIncr = (sens) => {
    if (duree.unite === 'mois') {
      set('dureeUnite', { ...duree, moisExact: Math.max(0, dureeMois + sens) });
    } else {
      // Un cran = UNE année pleine, à partir de l'année ARRONDIE courante —
      // c'est ici que « la virgule » disparaît (26 mois → 2,17 ans affichés,
      // +1 cran → 3 ans PILE = 36 mois, pas 2,17+1). Repasser en mois montre
      // toujours le compte exact, lui n'arrondit jamais.
      const anneesRondes = Math.max(0, Math.round(dureeMois / 12) + sens);
      set('dureeUnite', { ...duree, moisExact: anneesRondes * 12 });
    }
  };
  const basculerUniteDuree = () => set('dureeUnite', { ...duree, unite: duree.unite === 'annee' ? 'mois' : 'annee' });

  // Aperçu — mensualité que ces paramètres produiraient, pour rassurer AVANT
  // d'enregistrer (mode « params » seulement : en mode « mensualité », c'est
  // l'utilisateur qui la connaît déjà, c'est le champ Montant de la feuille).
  const apercu = useMemo(() => {
    if (!params || !nb(v.capital) || !dureeMois) return null;
    const sched = calculerCredit({
      actif: true, mode: 'params', capital: nb(v.capital), taux: nb(v.taux),
      duree: dureeMois, differe: entier(v.differe), assurance: nb(v.assurance),
      dateCapital: v.dateCapital || '', deblocages: v.deblocages || [],
      rembCapitalDeblocage: !!v.rembCapitalDeblocage, paliers: v.paliers || [],
    }, debutLigne, montantLigne, jourLigne);
    return sched && !sched.invalide ? sched : null;
  }, [v, params, debutLigne, montantLigne, jourLigne]);

  const setLigneListe = (cle, i, patch) => {
    const liste = [...(v[cle] || [])];
    liste[i] = { ...liste[i], ...patch };
    set(cle, liste);
  };
  const ajouter = (cle, vide) => set(cle, [...(v[cle] || []), vide]);
  const retirer = (cle, i) => set(cle, (v[cle] || []).filter((_, j) => j !== i));

  return (
    <View>
      <Text style={s.lbl}>{t('finances.creditMode')}</Text>
      <ChoixPuces
        options={[
          { id: 'mensualite', label: t('finances.creditModeMensualite') },
          { id: 'params', label: t('finances.creditModeParams') },
        ]}
        value={v.mode || 'mensualite'} onChange={(m) => set('mode', m)} />
      <Text style={s.aide}>{t(params ? 'finances.creditModeParamsAide' : 'finances.creditModeMensualiteAide')}</Text>

      <Text style={s.lbl}>{t('finances.creditCapital')}</Text>
      <TextInput style={s.input} value={v.capital || ''} onChangeText={(x) => set('capital', x.replace(/[^0-9.,]/g, ''))}
        keyboardType="decimal-pad" placeholder="0,00" placeholderTextColor={UI.faint} />

      <Text style={s.lbl}>{t('finances.creditTaux')}</Text>
      <TextInput style={s.input} value={v.taux || ''} onChangeText={(x) => set('taux', x.replace(/[^0-9.,]/g, ''))}
        keyboardType="decimal-pad" placeholder="0" placeholderTextColor={UI.faint} />

      <Text style={s.lbl}>{t('finances.creditDateRef')}</Text>
      <TouchableOpacity style={[s.input, s.champBtn]} activeOpacity={0.7}
        onPress={() => onDemanderMois?.(v.dateCapital, (mk) => set('dateCapital', mk), true)}>
        <Text style={[s.champTxt, !v.dateCapital && { color: UI.faint }]}>
          {v.dateCapital ? libelleMois(v.dateCapital, t) : t('finances.creditDateRefVide')}
        </Text>
      </TouchableOpacity>
      <Text style={s.aide}>{t(params ? 'finances.creditDateRefAideParams' : 'finances.creditDateRefAideMensualite')}</Text>

      {params && (
        <>
          {/* Durée ET Différé sur la MÊME ligne (Cédric, 17/08). Le libellé
              de Durée n'a plus « (mois) » — il ne se mettait pas à jour en
              année et mentait la moitié du temps ; le bouton d'unité juste
              à côté dit déjà dans quelle unité on est.
              `flexWrap` : la rangée est large (compteur + bouton mois/année
              + compteur), elle repasse sur deux lignes plutôt que de
              chevaucher si l'écran est étroit ou le texte agrandi. */}
          <View style={[s.row, { flexWrap: 'wrap', rowGap: 10, columnGap: 20 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={s.lblInline}>{t('finances.creditDuree')}</Text>
              <Stepper valeur={String(dureeAffichee).replace('.', ',')}
                onMoins={() => dureeIncr(-1)} onPlus={() => dureeIncr(1)} />
              <TouchableOpacity style={s.uniteBtn} activeOpacity={0.7} onPress={basculerUniteDuree}>
                <Text style={s.uniteBtnTxt}>{t(duree.unite === 'annee' ? 'commun.annee' : 'commun.mois')}</Text>
                <Text style={s.uniteRot}>⇄</Text>
              </TouchableOpacity>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={s.lblInline}>{t('finances.creditDiffere')}</Text>
              <Stepper valeur={entier(v.differe)}
                onMoins={() => set('differe', String(Math.max(0, entier(v.differe) - 1)))}
                onPlus={() => set('differe', String(entier(v.differe) + 1))} />
            </View>
          </View>
        </>
      )}

      <Text style={s.lbl}>{t('finances.creditAssurance')}</Text>
      <TextInput style={s.input} value={v.assurance || ''} onChangeText={(x) => set('assurance', x.replace(/[^0-9.,]/g, ''))}
        keyboardType="decimal-pad" placeholder="0,00" placeholderTextColor={UI.faint} />

      {!!apercu && (
        <Text style={s.aide}>{t('finances.creditMensualiteEstimee', { m: formatEuro(apercu.mensualiteTotale) })}</Text>
      )}

      {params && (
        <SectionPliable emoji="🧮" titre={t('finances.creditAvance')}
          sousTitre={[
            (v.paliers || []).length ? `${(v.paliers || []).length} ${t('finances.creditPaliers').toLowerCase()}` : '',
            (v.deblocages || []).length ? `${(v.deblocages || []).length} ${t('finances.creditDeblocages').toLowerCase()}` : '',
          ].filter(Boolean).join('  ·  ') || null}
          ouvert={avanceOuvert} onToggle={onToggleAvance}>

          <Text style={s.lbl}>{t('finances.creditPaliers')}</Text>
          <Text style={s.aide}>{t('finances.creditPaliersAide')}</Text>
          {(v.paliers || []).map((p, i) => (
            <View key={i} style={s.ligneListe}>
              <TextInput style={[s.input, s.champCourt]} value={p.mensualite || ''}
                onChangeText={(x) => setLigneListe('paliers', i, { mensualite: x.replace(/[^0-9.,]/g, '') })}
                keyboardType="decimal-pad" placeholder={t('finances.creditPalierMensualite')} placeholderTextColor={UI.faint} />
              <TextInput style={[s.input, s.champCourt]} value={p.duree || ''}
                onChangeText={(x) => setLigneListe('paliers', i, { duree: x.replace(/[^0-9]/g, '') })}
                keyboardType="number-pad" placeholder={t('finances.creditPalierDuree')} placeholderTextColor={UI.faint} />
              <TouchableOpacity onPress={() => retirer('paliers', i)} hitSlop={HIT}>
                <Text style={s.suppr}>🗑️</Text>
              </TouchableOpacity>
            </View>
          ))}
          <TouchableOpacity style={s.ajouter} activeOpacity={0.7}
            onPress={() => ajouter('paliers', { mensualite: '', duree: '' })}>
            <Text style={s.ajouterTxt}>＋ {t('finances.creditAjouterPalier')}</Text>
          </TouchableOpacity>

          <Text style={[s.lbl, { marginTop: 16 }]}>{t('finances.creditDeblocages')}</Text>
          <Text style={s.aide}>{t('finances.creditDeblocagesAide')}</Text>
          {(v.deblocages || []).map((d, i) => (
            <View key={i} style={s.ligneListe}>
              <TouchableOpacity style={[s.input, s.champBtn, s.champCourt]} activeOpacity={0.7}
                onPress={() => onDemanderMois?.(d.date, (mk) => setLigneListe('deblocages', i, { date: mk }))}>
                <Text style={[s.champTxt, !d.date && { color: UI.faint }]}>
                  {d.date ? libelleMois(d.date, t) : 'AAAA-MM'}
                </Text>
              </TouchableOpacity>
              <TextInput style={[s.input, s.champCourt]} value={d.pct || ''}
                onChangeText={(x) => setLigneListe('deblocages', i, { pct: x.replace(/[^0-9.,]/g, '') })}
                keyboardType="decimal-pad" placeholder="%" placeholderTextColor={UI.faint} />
              <TouchableOpacity onPress={() => retirer('deblocages', i)} hitSlop={HIT}>
                <Text style={s.suppr}>🗑️</Text>
              </TouchableOpacity>
            </View>
          ))}
          <TouchableOpacity style={s.ajouter} activeOpacity={0.7}
            onPress={() => ajouter('deblocages', { date: '', pct: '' })}>
            <Text style={s.ajouterTxt}>＋ {t('finances.creditAjouterDeblocage')}</Text>
          </TouchableOpacity>

          {(v.deblocages || []).length > 0 && (
            <TouchableOpacity style={s.checkRow} activeOpacity={0.7}
              onPress={() => set('rembCapitalDeblocage', !v.rembCapitalDeblocage)}>
              <Text style={s.checkBox}>{v.rembCapitalDeblocage ? '☑' : '☐'}</Text>
              <Text style={s.checkTxt}>{t('finances.creditRembCapitalDeblocage')}</Text>
            </TouchableOpacity>
          )}
        </SectionPliable>
      )}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  // Bouton d'unité — mêmes proportions que celui de `SelecteurDuree` (Core),
  // repris ici pour rester visuellement cohérent sans dépendre du composant
  // (dont la logique de non-conversion ne convient pas à ce champ).
  uniteBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, marginLeft: 6, paddingHorizontal: 14, paddingVertical: 7, borderRadius: 11, borderWidth: 1, borderColor: UI.accent, backgroundColor: UI.card },
  uniteBtnTxt: { fontSize: 14.5, fontWeight: '800', color: UI.accent },
  uniteRot: { fontSize: 12, color: UI.faint, fontWeight: '700' },
  // Libellé EN LIGNE (Durée / Différé, 17/08) — partout ailleurs dans la
  // feuille le libellé est au-dessus (`s.lbl`, via `stylesForm`) ; ici les
  // deux champs partagent une rangée, le libellé passe donc à côté.
  lblInline: { fontSize: 12.5, fontWeight: '700', color: UI.muted },
  champTxt: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  aide: { fontSize: 12, color: UI.faint, lineHeight: 17, marginTop: 5 },
  ligneListe: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 },
  champCourt: { flex: 1 },
  suppr: { fontSize: 16, padding: 4 },
  ajouter: { marginTop: 10, alignSelf: 'flex-start' },
  ajouterTxt: { fontSize: 13, fontWeight: '700', color: UI.accent },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 14 },
  checkBox: { fontSize: 18, color: UI.accent },
  checkTxt: { fontSize: 13, color: UI.ink, flex: 1 },
});
